import { Link } from '@inertiajs/react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';

interface FooterItem {
    label: string;
    href: string;
}

interface FooterSection {
    id: string;
    title: string;
    items: FooterItem[];
}

const FOOTER_SECTIONS: FooterSection[] = [
    {
        id: 'comprar',
        title: 'COMPRAR',
        items: [{ label: 'Classificados', href: '/search?type=embarcacoes' }],
    },
    {
        id: 'vender',
        title: 'VENDER',
        items: [
            { label: 'Vender minha embarcação', href: '/vender' },
            { label: 'Gerenciar meu negócio', href: '/planos' },
            { label: 'Anunciar minha empresa', href: '/servicos/cadastro' },
            { label: 'Dashboard do anunciante', href: '/login' },
        ],
    },
    {
        id: 'solucoes',
        title: 'SOLUÇÕES',
        items: [
            { label: 'Consórcio', href: 'https://page.comprenautica.com.br/consorciocomprenautica' },
            { label: 'Seguros', href: 'https://page.comprenautica.com.br/seguros' },
        ],
    },
    {
        id: 'editorial',
        title: 'EDITORIAL',
        items: [
            { label: 'CN Notícias', href: 'https://comprenautica.com.br/blog/' },
            { label: 'Canal Náutico', href: 'https://www.youtube.com/c/CompreN%C3%A1utica' },
            { label: 'Check-ups', href: 'https://www.youtube.com/playlist?list=PLEFAEXAfplX2QzTcPExd3GUh0mODcCaAm' },
        ],
    },
    {
        id: 'sobre-nos',
        title: 'SOBRE NÓS',
        items: [
            { label: 'Institucional', href: '/sobre-nos' },
            { label: 'Contato', href: '/contato' },
        ],
    },
    {
        id: 'faq',
        title: 'FAQ',
        items: [
            { label: 'Perguntas e respostas', href: '/faq' },
            { label: 'Termos de uso e Política de Privacidade', href: '/terms' },
            { label: 'LGPD', href: '/lgpd' },
        ],
    },
];

const isExternalUrl = (href: string) => /^https?:\/\//i.test(href);

function FooterNavLink({ item, className }: { item: FooterItem; className: string }) {
    if (isExternalUrl(item.href)) {
        return (
            <a href={item.href} className={className} target="_blank" rel="noopener noreferrer">
                {item.label}
            </a>
        );
    }

    return (
        <Link href={item.href} className={className}>
            {item.label}
        </Link>
    );
}

export default function Footer() {
    const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

    const toggleSection = (section: string) => {
        setExpandedSections((prev) => ({
            ...prev,
            [section]: !prev[section],
        }));
    };

    return (
        <footer className="relative z-10 -mt-[40vh] mt-(--wave-height) md:-mt-[1vh] md:[--wave-height:--spacing(16)]">
            {/* Mobile Footer */}
            <div className="md:hidden">
                {/* Main Footer Section */}
                <div className="bg-[#041F4A] text-white">
                    <div className="footer-links-container px-5 py-6">
                        {FOOTER_SECTIONS.map((section) => (
                            <div key={section.id} className="border-b border-[#DCE1EA] last:border-b-0">
                                <button onClick={() => toggleSection(section.id)} className="flex w-full items-center justify-between py-4 text-left">
                                    <span className="text-[18px] leading-[22px] font-normal text-[#FAFAFA] uppercase">{section.title}</span>
                                    {expandedSections[section.id] ? (
                                        <ChevronUp className="h-5 w-5 text-[#FAFAFA]" />
                                    ) : (
                                        <ChevronDown className="h-5 w-5 text-[#FAFAFA]" />
                                    )}
                                </button>

                                {expandedSections[section.id] && (
                                    <div className="pb-4">
                                        <ul className="space-y-2">
                                            {section.items.map((item) => (
                                                <li key={`${section.id}-${item.label}`}>
                                                    <FooterNavLink
                                                        item={item}
                                                        className="block py-1 text-[14px] leading-[17px] font-normal text-[#FAFAFA] hover:text-white/80"
                                                    />
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Bottom Section */}
                <div className="bg-[#041F4A] text-white">
                    <div className="px-24 py-4">
                        <div className="flex w-full items-center justify-center">
                            <div className="flex w-full items-center justify-center">
                                <img src="/cn_logo_wide.png" alt="Compre Nautica Logo Larga" className="" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Desktop Footer */}
            <div className="hidden md:block">
                {/* Wave top separator over dark background */}
                <div className="relative bg-[#071b3f]">
                    <svg
                        className="absolute -top-(--wave-height) left-0 h-16 w-full"
                        viewBox="0 0 1440 100"
                        preserveAspectRatio="none"
                        aria-hidden="true"
                    >
                        <path d="M0,0 C200,100 520,100 720,50 C940,0 1240,0 1440,0 L1440,100 L0,100 Z" fill="#071b3f" />
                    </svg>

                    {/* Links grid */}
                    <div className="mx-4 py-8 sm:mx-6 sm:py-12 lg:mx-24 lg:py-14">
                        <div className="footer-links-container grid grid-cols-1 gap-6 text-white/90 sm:grid-cols-2 sm:gap-8 lg:grid-cols-6 lg:gap-10">
                            {FOOTER_SECTIONS.map((section) => (
                                <div key={section.id}>
                                    <h4 className="mb-3 text-xs font-bold tracking-wide text-white/90 uppercase sm:mb-4 sm:text-sm lg:mb-5">
                                        {section.title}
                                    </h4>
                                    <ul className="space-y-3 text-white/70">
                                        {section.items.map((item) => (
                                            <li key={`${section.id}-${item.label}`}>
                                                <FooterNavLink item={item} className="hover:text-white" />
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Bottom bar */}
                <div className="bg-[#071b3f] text-white">
                    <div className="mx-4 flex flex-col items-center justify-center gap-4 py-4 text-sm sm:mx-6 sm:flex-row sm:gap-0 lg:mx-24">
                        <div className="flex items-center justify-center">
                            <img src="/cn_logo_wide.png" alt="Compre Nautica Logo Larga" className="max-h-12" />
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
}
